/**
 * A tiny drawing vocabulary, in PDF points (1/72 inch), origin top-left.
 *
 * Layout produces these; a renderer consumes them. Keeping the two apart means
 * the page geometry is a pure function that can be tested without a PDF
 * library, and a second renderer — an on-screen preview, a PNG, a plotter —
 * costs about forty lines.
 */

import type { QrMatrix } from "../qr";

export type FontFamily = "display" | "body" | "mono";
export type FontWeight = "normal" | "bold";
export type TextAlign = "left" | "center" | "right";

export interface TextOp {
  op: "text";
  x: number;
  /** Baseline. */
  y: number;
  text: string;
  size: number;
  family: FontFamily;
  weight: FontWeight;
  color: string;
  align: TextAlign;
  /** Extra tracking in points per character, for the display face. */
  tracking?: number;
}

export interface RectOp {
  op: "rect";
  x: number;
  y: number;
  w: number;
  h: number;
  fill?: string;
  stroke?: string;
  lineWidth?: number;
  /** On/off lengths in points. */
  dash?: [number, number];
}

export interface LineOp {
  op: "line";
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  lineWidth: number;
  dash?: [number, number];
}

export interface QrOp {
  op: "qr";
  /** Top-left of the code including its quiet zone. */
  x: number;
  y: number;
  /** Side length including the quiet zone. */
  size: number;
  quietModules: number;
  matrix: QrMatrix;
}

export type DrawOp = TextOp | RectOp | LineOp | QrOp;

export interface Page {
  widthPt: number;
  heightPt: number;
  ops: DrawOp[];
}

export const PAGE_SIZES = {
  a4: { widthPt: 595.28, heightPt: 841.89, label: "A4" },
  letter: { widthPt: 612, heightPt: 792, label: "US Letter" },
} as const;

export type PageSizeKey = keyof typeof PAGE_SIZES;

export const MM = 72 / 25.4;
